import { buildAdPath } from 'lib/ads';
import { useFetchAd } from 'lib/ads/dai/fetchAd';
import { useEffect, useMemo, useState } from 'react';
import { useSessionstorageState } from 'rooks';

import { Video } from 'api';
import { getPlayerZone } from 'components/pages/embed/zones';
import { useTalkPageContext } from 'components/pages/talks/slug/talk-page-context';
import { useRouter } from 'next/router';
import { usePageTargeting } from 'lib/ads/usePageTargeting';

type UseAdsProps = {
  player: HTMLVideoElement | null;
  playerTalkData: Video['playerData'];
  adContainerRef: React.RefObject<HTMLDivElement>;
};

interface UseAdsReturn {
  adProps: {
    adStreamId: string | undefined;
    adLoading: boolean;
    adError: string | null;
    adTimeRemaining: string | undefined;
    isAdBreak: boolean;
    isSkipCountdownActive: boolean;
  };
  mainContentSrc: string | null | undefined;
  isAdBreak: boolean;
}

export const useAds = ({
  player,
  playerTalkData,
  adContainerRef
}: UseAdsProps): UseAdsReturn => {
  const router = useRouter();
  const isEmbed = router.asPath.includes('embed');

  // If it's an embed, return early with default values
  if (isEmbed) {
    return {
      adProps: {
        adStreamId: undefined,
        adLoading: false,
        adError: null,
        adTimeRemaining: undefined,
        isAdBreak: false,
        isSkipCountdownActive: false
      },
      mainContentSrc: playerTalkData?.resources?.hls?.stream,
      isAdBreak: false
    };
  }

  const { zone, partner } = router.query;
  const { autoplay } = useTalkPageContext();

  const [mainContentSrc, setMainContentSrc] = useState<string | null>();
  const [adParams, setAdParams] = useState<Record<string, string>>({});

  const playerZone = useMemo(() => {
    if (partner) {
      return getPlayerZone(partner as string);
    }

    if (zone) {
      return getPlayerZone(zone as string);
    }

    return undefined;
  }, [zone, partner]);

  const [muted] = useSessionstorageState('muted_state');
  const adTag = buildAdPath(process.env.NEXT_PUBLIC_AD_PATH_TALKS as string);
  const contentSourceId =
    playerTalkData.mediaProjectVersionIdentifier ||
    playerTalkData.mediaIdentifier;

  // Extract talk slug for page targeting
  const talkSlug = useMemo(() => {
    return playerTalkData?.resources?.hls?.maiTargeting?.talk as string;
  }, [playerTalkData]);

  // Set page-level targeting for this talk
  usePageTargeting({ talk: talkSlug });

  useEffect(() => {
    const adTagParameters: { [key: string]: string } = {
      sz: '854x480',
      ciu_szs: '300x250,320x50,728x90',
      vid: String(contentSourceId),
      vpa: autoplay ? 'auto' : 'click',
      output: 'vast',
      // Req for Monetization plcmt is regarding placement of ad
      plcmt: '1',
      // Req for Monetization vpmute tells GAM whether ad plays while player is muted or not, 1 for muted 0 for unmuted.
      vpmute: muted ? '1' : '0',
      ad_rule: '1',
      wta: '1',
      ...Object.fromEntries(
        Object.entries(playerZone?.ad_targeting || {}).map(([k, v]) => [
          k,
          String(v)
        ])
      )
    };

    if (adTag) {
      adTagParameters.iu = adTag;
    }

    adTagParameters.pp = 'DAI%20Video%20restrictions';

    const custParams: string[] = [];

    if (
      playerTalkData?.resources?.hls &&
      playerTalkData?.resources?.hls?.maiTargeting
    ) {
      Object.entries(playerTalkData?.resources?.hls.maiTargeting).forEach(
        ([key, value]) => {
          custParams.push(
            `${encodeURIComponent(key)}=${encodeURIComponent(value as string)}`
          );
        }
      );

      const descriptionUrl = `https://www.ted.com/talks/${playerTalkData?.resources?.hls.maiTargeting.talk}`;

      custParams.push('redesign=talkpage');
      if (process.env.APP_ENV !== 'production') custParams.push('test=true');
      custParams.push(`description_url=${encodeURIComponent(descriptionUrl)}`);
      adTagParameters.description_url = descriptionUrl;
    }

    adTagParameters.cust_params = custParams.join('&');
    setAdParams(adTagParameters);
  }, []);

  const {
    isAdBreak,
    adError,
    loading: adLoading,
    adTimeRemaining,
    isSkipCountdownActive,
    adStreamId
  } = useFetchAd({
    player,
    adContainerRef,
    maiTargeting: playerTalkData?.resources?.hls?.maiTargeting,
    adTag,
    adParams,
    playbackSrc: playerTalkData?.resources?.hls?.stream,
    setContentSrc: setMainContentSrc,
    mainContentSrc: mainContentSrc || null,
    contentSourceId
  });

  return {
    adProps: {
      adStreamId,
      adLoading,
      adError,
      adTimeRemaining,
      isAdBreak,
      isSkipCountdownActive
    },
    mainContentSrc,
    isAdBreak
  };
};
